import { CUSTOMER_PHONE, phoneError } from '@theme/dew-delivery-rules';
import { syncOrderNote } from '@theme/dew-order-note';
/**
 * Required buyer phone in the cart summary (snippets/dew-cart-phone.liquid). Saved to the cart as the
 * "Customer phone" attribute on change, so it survives drawer re-renders and lands on the order.
 * The cart checkout gate in dew-cart-details.js awaits save() before letting checkout start.
 */
const root = () => window.Shopify?.routes?.root || '/';
class DewCartPhone extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.input = this.querySelector('input');
    this.saved = this.input.dataset.saved || '';
    this.pending = Promise.resolve();
    const options = { signal: this.abort.signal };
    this.input.addEventListener('input', () => this.validate(), options);
    // Save quietly on change; the error bubble and refocus belong to the checkout attempt, not to tabbing away.
    this.input.addEventListener('change', () => { if (!this.validate()) this.save().catch(() => {}); }, options);
    // A cart re-render can morph in a number saved elsewhere; keep the saved value and validity in step with it.
    this.observer = new MutationObserver(() => { this.saved = this.input.dataset.saved || ''; this.validate(); });
    this.observer.observe(this.input, { attributes: true, attributeFilter: ['value', 'data-saved'] });
    this.validate();
  }
  disconnectedCallback() { this.abort?.abort(); this.abort = null; this.observer?.disconnect(); }
  validate() {
    const error = phoneError(this.input.value);
    this.input.setCustomValidity(error);
    return error;
  }
  /** Resolves once the current value is on the cart; rejects (and shows the error) when it isn't a usable phone. */
  save() {
    const error = this.validate();
    if (error) { this.input.reportValidity(); this.input.focus(); return Promise.reject(new Error(error)); }
    const phone = this.input.value.trim();
    this.pending = this.pending.catch(() => {}).then(async () => {
      if (phone === this.saved) return;
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [CUSTOMER_PHONE]: phone } }),
      });
      if (!response.ok) throw new Error('We couldn’t save your phone number. Please try again.');
      this.saved = phone;
      this.input.dataset.saved = phone;
      await syncOrderNote();
    });
    return this.pending;
  }
}
if (!customElements.get('dew-cart-phone')) customElements.define('dew-cart-phone', DewCartPhone);
